// src/features/booking/PolicyEditor.tsx
// Admin panel: edit the booking policy for a resource or a booking type.
// GetEffectivePolicy only resolves for a specific resource — a booking-type
// target can be upserted but not pre-loaded (no "get policy by type" RPC).
//
// Settings-row surface (docs/ux/14-forms.md #3): label-left rows grouped into
// sections, content-sized controls, one Save button pinned to the panel
// since these fields must apply atomically.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import { FormRow, SegmentedControl } from '@/components/form';
import { Select } from '@/components/Select';
import type { SelectOption } from '@/components/Select';
import { durationToHours, errMessage, hoursToDuration, durationToMinutes, minutesToDuration } from './lib';
import { GetEffectivePolicy } from '@core/api/endpoints/booking/get_effective_policy';
import { UpsertPolicy } from '@core/api/endpoints/booking/upsert_policy';
import type { BookingPolicy, BookingTypeDefinition, CancellationTier } from '@core/types/booking';

function digits(v: string, allowDot = false): string {
  return allowDot ? v.replace(/[^0-9.]/g, '') : v.replace(/[^0-9]/g, '');
}

const emptyPolicy = (): BookingPolicy => ({
  name: '',
  description: '',
  allowed_booking_type_ids: [],
  cancellation_tiers: [],
  deposit_required: false,
  requires_approval: false,
});

interface Props {
  bookingTypes: BookingTypeDefinition[];
  scope: { productId: string; platformId: string; tenantId: string };
}

/** Clearable numeric row — empty means unset, not 0. No steppers. */
function NumberField({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
}) {
  return (
    <input
      type="text"
      inputMode="numeric"
      value={value}
      onChange={(e) => onChange(digits(e.target.value))}
      placeholder="—"
      aria-label={ariaLabel}
      className="w-20 rounded-md border border-input bg-background px-2.5 py-1 text-right text-sm tabular-nums text-foreground outline-none focus:ring-2 focus:ring-ring"
    />
  );
}

export function PolicyEditor({ bookingTypes, scope }: Props) {
  const { t } = useI18n();
  const [targetKind, setTargetKind] = useState<'resource' | 'type'>('resource');
  const [resourceId, setResourceId] = useState('');
  const [typeId, setTypeId] = useState(bookingTypes[0]?.entity?.id ?? '');
  const [policy, setPolicy] = useState<BookingPolicy>(emptyPolicy());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const typeOptions: SelectOption[] = bookingTypes.map((bt) => ({ value: bt.entity?.id ?? '', label: bt.display_name || bt.slug }));

  const loadEffective = async () => {
    if (!resourceId.trim()) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const res = await GetEffectivePolicy(resourceId.trim());
      setPolicy(res.policy ?? emptyPolicy());
    } catch (e) {
      setError(errMessage(e, t('booking.admin.policies.loadError')));
    } finally {
      setLoading(false);
    }
  };

  const addTier = () => {
    setPolicy((p) => ({ ...p, cancellation_tiers: [...(p.cancellation_tiers ?? []), { before_start: '86400s', penalty_percent: 50 }] }));
  };
  const updateTier = (i: number, tier: CancellationTier) => {
    setPolicy((p) => ({ ...p, cancellation_tiers: p.cancellation_tiers.map((t2, idx) => (idx === i ? tier : t2)) }));
  };
  const removeTier = (i: number) => {
    setPolicy((p) => ({ ...p, cancellation_tiers: p.cancellation_tiers.filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    if (!policy.name.trim()) {
      setError(t('booking.admin.policies.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const target = targetKind === 'resource'
        ? { bookable_resource: { type_id: '', type_name: 'bookable_resource', id: resourceId.trim(), platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId } }
        : { booking_type_id: typeId };
      await UpsertPolicy({
        entity: { type_id: '', type_name: 'platform', id: scope.platformId, platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId },
        policy: { ...policy, ...target },
      });
      setSaving(false);
      setNotice(t('booking.admin.policies.saved'));
    } catch (e) {
      setSaving(false);
      setError(errMessage(e, t('booking.admin.policies.saveError')));
    }
  };

  return (
    <div className="max-w-3xl space-y-1 rounded-xl border border-border bg-card px-5 py-1">
      {error && <div className="pt-3"><Alert variant="error">{error}</Alert></div>}
      {notice && <div className="pt-3"><Alert variant="success">{notice}</Alert></div>}

      <FormRow label={t('booking.admin.policies.target')}>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            aria-label={t('booking.admin.policies.target')}
            value={targetKind}
            onChange={(v) => setTargetKind(v as 'resource' | 'type')}
            options={[
              { value: 'resource', label: t('booking.admin.policies.targetResource') },
              { value: 'type', label: t('booking.admin.policies.targetType') },
            ]}
          />
          {targetKind === 'resource' ? (
            <>
              <input
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
                placeholder={t('booking.admin.policies.resourceId')}
                aria-label={t('booking.admin.policies.resourceId')}
                className="w-56 rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              />
              <Button size="sm" variant="secondary" onClick={loadEffective} isLoading={loading} disabled={!resourceId.trim()}>
                {t('booking.admin.policies.loadEffective')}
              </Button>
            </>
          ) : (
            <div className="w-56">
              <Select value={typeId} onChange={(e) => setTypeId(e.target.value)} options={typeOptions} aria-label={t('booking.admin.policies.bookingType')} fullWidth />
            </div>
          )}
        </div>
      </FormRow>

      <FormRow label={t('booking.admin.policies.field.name')} htmlFor="policy-name">
        <input
          id="policy-name"
          value={policy.name}
          onChange={(e) => setPolicy((p) => ({ ...p, name: e.target.value }))}
          className="w-72 rounded-md border border-input bg-background px-2.5 py-1 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
        />
      </FormRow>
      <FormRow label={t('booking.admin.policies.field.description')} htmlFor="policy-description">
        <input
          id="policy-description"
          value={policy.description}
          onChange={(e) => setPolicy((p) => ({ ...p, description: e.target.value }))}
          className="w-full max-w-md rounded-md border border-input bg-background px-2.5 py-1 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
        />
      </FormRow>

      <div className="border-t border-border pt-1">
        <p className="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('booking.admin.policies.section.timing')}</p>
        <FormRow label={t('booking.admin.policies.field.minNoticeHours')}>
          <NumberField value={String(durationToHours(policy.min_notice) || '')} onChange={(v) => setPolicy((p) => ({ ...p, min_notice: hoursToDuration(parseFloat(v) || 0) }))} ariaLabel={t('booking.admin.policies.field.minNoticeHours')} />
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.maxDaysAhead')}>
          <NumberField value={policy.max_days_ahead ? String(policy.max_days_ahead) : ''} onChange={(v) => setPolicy((p) => ({ ...p, max_days_ahead: parseInt(v, 10) || undefined }))} ariaLabel={t('booking.admin.policies.field.maxDaysAhead')} />
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.minDurationMinutes')}>
          <NumberField value={String(durationToMinutes(policy.min_duration) || '')} onChange={(v) => setPolicy((p) => ({ ...p, min_duration: minutesToDuration(parseInt(v, 10) || 0) }))} ariaLabel={t('booking.admin.policies.field.minDurationMinutes')} />
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.maxDurationMinutes')}>
          <NumberField value={String(durationToMinutes(policy.max_duration) || '')} onChange={(v) => setPolicy((p) => ({ ...p, max_duration: minutesToDuration(parseInt(v, 10) || 0) }))} ariaLabel={t('booking.admin.policies.field.maxDurationMinutes')} />
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.bufferMinutes')}>
          <NumberField value={String(durationToMinutes(policy.buffer_between) || '')} onChange={(v) => setPolicy((p) => ({ ...p, buffer_between: minutesToDuration(parseInt(v, 10) || 0) }))} ariaLabel={t('booking.admin.policies.field.bufferMinutes')} />
        </FormRow>
      </div>

      <div className="border-t border-border pt-1">
        <p className="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('booking.admin.policies.section.limits')}</p>
        <FormRow label={t('booking.admin.policies.field.maxPerDay')}>
          <NumberField value={policy.max_bookings_per_day ? String(policy.max_bookings_per_day) : ''} onChange={(v) => setPolicy((p) => ({ ...p, max_bookings_per_day: parseInt(v, 10) || undefined }))} ariaLabel={t('booking.admin.policies.field.maxPerDay')} />
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.maxActivePerCustomer')}>
          <NumberField value={policy.max_active_bookings_per_customer ? String(policy.max_active_bookings_per_customer) : ''} onChange={(v) => setPolicy((p) => ({ ...p, max_active_bookings_per_customer: parseInt(v, 10) || undefined }))} ariaLabel={t('booking.admin.policies.field.maxActivePerCustomer')} />
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.maxReschedules')}>
          <NumberField value={policy.max_reschedules ? String(policy.max_reschedules) : ''} onChange={(v) => setPolicy((p) => ({ ...p, max_reschedules: parseInt(v, 10) || undefined }))} ariaLabel={t('booking.admin.policies.field.maxReschedules')} />
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.rescheduleNoticeHours')}>
          <NumberField value={String(durationToHours(policy.reschedule_notice) || '')} onChange={(v) => setPolicy((p) => ({ ...p, reschedule_notice: hoursToDuration(parseFloat(v) || 0) }))} ariaLabel={t('booking.admin.policies.field.rescheduleNoticeHours')} />
        </FormRow>
      </div>

      <div className="border-t border-border pt-1">
        <p className="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('booking.admin.policies.section.approvalAndRisk')}</p>
        <FormRow label={t('booking.admin.policies.field.requiresApproval')}>
          <button
            type="button"
            role="switch"
            aria-checked={policy.requires_approval}
            aria-label={t('booking.admin.policies.field.requiresApproval')}
            onClick={() => setPolicy((p) => ({ ...p, requires_approval: !p.requires_approval }))}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${policy.requires_approval ? 'bg-primary' : 'bg-muted'}`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-sm transition-transform ${policy.requires_approval ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.depositRequired')}>
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={policy.deposit_required}
              aria-label={t('booking.admin.policies.field.depositRequired')}
              onClick={() => setPolicy((p) => ({ ...p, deposit_required: !p.deposit_required }))}
              className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${policy.deposit_required ? 'bg-primary' : 'bg-muted'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-sm transition-transform ${policy.deposit_required ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
            {policy.deposit_required && (
              <span className="inline-flex items-center gap-1.5">
                <NumberField value={policy.deposit_percent ? String(policy.deposit_percent) : ''} onChange={(v) => setPolicy((p) => ({ ...p, deposit_percent: parseInt(v, 10) || undefined }))} ariaLabel={t('booking.admin.policies.field.depositPercent')} />
                <span className="text-xs text-muted-foreground">%</span>
              </span>
            )}
          </div>
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.noShowPenaltyPercent')}>
          <NumberField value={policy.no_show_penalty_percent ? String(policy.no_show_penalty_percent) : ''} onChange={(v) => setPolicy((p) => ({ ...p, no_show_penalty_percent: parseInt(v, 10) || undefined }))} ariaLabel={t('booking.admin.policies.field.noShowPenaltyPercent')} />
        </FormRow>
        <FormRow label={t('booking.admin.policies.field.noShowStrikeLimit')}>
          <NumberField value={policy.no_show_strike_limit ? String(policy.no_show_strike_limit) : ''} onChange={(v) => setPolicy((p) => ({ ...p, no_show_strike_limit: parseInt(v, 10) || undefined }))} ariaLabel={t('booking.admin.policies.field.noShowStrikeLimit')} />
        </FormRow>
      </div>

      <div className="border-t border-border py-3">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">{t('booking.admin.policies.cancellationTiers')}</span>
          <Button size="sm" variant="secondary" onClick={addTier} type="button">
            {t('common.add')}
          </Button>
        </div>
        <div className="space-y-1.5">
          {(policy.cancellation_tiers ?? []).map((tier, i) => (
            <div key={i} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5">
              <NumberField value={String(durationToHours(tier.before_start) || '')} onChange={(v) => updateTier(i, { ...tier, before_start: hoursToDuration(parseFloat(v) || 0) })} ariaLabel={t('booking.admin.policies.field.tierHoursBefore')} />
              <span className="text-xs text-muted-foreground">{t('booking.admin.policies.field.tierHoursBefore')}</span>
              <span className="flex-1" />
              <NumberField value={String(tier.penalty_percent ?? '')} onChange={(v) => updateTier(i, { ...tier, penalty_percent: parseInt(v, 10) || 0 })} ariaLabel={t('booking.admin.policies.field.tierPenaltyPercent')} />
              <span className="text-xs text-muted-foreground">{t('booking.admin.policies.field.tierPenaltyPercent')}</span>
              <button type="button" onClick={() => removeTier(i)} aria-label={t('common.remove')} className="ml-2 text-muted-foreground hover:text-destructive">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </button>
            </div>
          ))}
          {(policy.cancellation_tiers ?? []).length === 0 && <p className="text-xs text-muted-foreground">{t('booking.admin.policies.noTiers')}</p>}
        </div>
      </div>

      <div className="flex justify-end border-t border-border py-3">
        <Button size="sm" onClick={save} isLoading={saving} disabled={!policy.name.trim()}>
          {t('booking.admin.policies.save')}
        </Button>
      </div>
    </div>
  );
}
